import { supabase } from '@/lib/supabase';
import { DEFAULT_GEO, type GeoOptions, type AlotMethod, type TzetMethod } from '@/lib/hebcal';

export interface UserProfile {
  id: string;
  email: string | null;
  fullName: string;
  phoneNumber: string | null;
  role: string;
  language: string;
  timezone: string;
  latitude: number | null;
  longitude: number | null;
  elevation: number | null;
  shabbatMode: string;
  holidayMode: string;
  alotMethod: AlotMethod;
  tzetMethod: TzetMethod;
}

let cached: { userId: string; profile: UserProfile } | null = null;
let inflight: Promise<UserProfile | null> | null = null;

/** Fetch the current user's profile row (cached per session). */
export async function fetchUserProfile(force = false): Promise<UserProfile | null> {
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user;
  if (!user) return null;

  if (!force && cached?.userId === user.id) return cached.profile;
  if (!force && inflight) return inflight;

  inflight = (async () => {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, full_name, phone_number, role, language, timezone, latitude, longitude, elevation, shabbat_mode, holiday_mode, alot_method, tzet_method')
      .eq('id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Failed to load user profile:', error);
      return null;
    }

    const profile: UserProfile = {
      id: user.id,
      email: data?.email ?? user.email ?? null,
      fullName: data?.full_name || (user.user_metadata?.full_name as string) || '',
      phoneNumber: data?.phone_number ?? null,
      role: data?.role || 'user',
      language: data?.language || 'en',
      timezone: data?.timezone || DEFAULT_GEO.timezone,
      latitude: data?.latitude ?? null,
      longitude: data?.longitude ?? null,
      elevation: data?.elevation ?? null,
      shabbatMode: data?.shabbat_mode || 'skip',
      holidayMode: data?.holiday_mode || 'skip',
      alotMethod: (data?.alot_method as AlotMethod) || '72min',
      tzetMethod: (data?.tzet_method as TzetMethod) || '72min',
    };
    cached = { userId: user.id, profile };
    return profile;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

/** Geo options for zmanim/calendar computation, from profile with defaults. */
export function profileGeo(profile: UserProfile | null): GeoOptions {
  if (!profile || profile.latitude == null || profile.longitude == null) {
    return {
      ...DEFAULT_GEO,
      timezone: profile?.timezone || DEFAULT_GEO.timezone,
      alotMethod: profile?.alotMethod || '72min',
      tzetMethod: profile?.tzetMethod || '72min',
    };
  }
  return {
    latitude: profile.latitude,
    longitude: profile.longitude,
    elevation: profile.elevation ?? 0,
    timezone: profile.timezone || DEFAULT_GEO.timezone,
    alotMethod: profile.alotMethod || '72min',
    tzetMethod: profile.tzetMethod || '72min',
  };
}

export function invalidateProfileCache(): void {
  cached = null;
}
